import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';

import { ThreadNotFoundPanel } from '../-components/empty-states';
import { ThreadPanel } from '../-components/thread-panel';
import type { Id } from '@minty/shared/ids';
import type { TrpcClient } from '~/lib/trpc';
import { DetailsLoading } from '~/components/list-panel-states';
import { getPostHog } from '~/lib/posthog';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/mail/$folder/$threadId')({
  pendingComponent: DetailsLoading,
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      context.trpc.mailbox.views.getThread.queryOptions({
        threadId: params.threadId as Id<'mailboxThreads'>,
      }),
    );
  },
  // A navigation, never a hover preload; switching conversations is a stay.
  onEnter: ({ context, params }) =>
    markRead(context.trpcClient, params.threadId),
  onStay: ({ context, params }) =>
    markRead(context.trpcClient, params.threadId),
  notFoundComponent: ThreadNotFoundPanel,
  component: ThreadRoute,
});

function ThreadRoute() {
  const { folder, threadId } = Route.useParams();
  const trpc = useTRPC();
  const { data: thread } = useSuspenseQuery(
    trpc.mailbox.views.getThread.queryOptions({
      threadId: threadId as Id<'mailboxThreads'>,
    }),
  );

  if (thread === null) {
    throw notFound();
  }

  return <ThreadPanel key={threadId} folder={folder} thread={thread} />;
}

function markRead(client: TrpcClient, threadId: string) {
  void client.mailbox.seen.markThreadRead
    .mutate({ threadId: threadId as Id<'mailboxThreads'> })
    .catch((error: unknown) => getPostHog()?.captureException(error));
}
